import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  FeatureAccess,
  LicenseAdapter,
  LicenseContextValue,
  LicenseFeature,
  LicenseFlags,
} from "../types/license";
import { createFallbackLicenseAdapter } from "../services/localLicenseAdapter";

const DEFAULT_FLAGS: LicenseFlags = {
  gatingEnabled: false,
  proUnlocked: false,
  bypassEnabled: false,
};

const LicenseContext = createContext<LicenseContextValue | null>(null);

function sanitizeFlags(flags: LicenseFlags, allowBypass: boolean): LicenseFlags {
  return {
    gatingEnabled: Boolean(flags.gatingEnabled),
    proUnlocked: Boolean(flags.proUnlocked),
    bypassEnabled: allowBypass && Boolean(flags.bypassEnabled),
  };
}

function computeFeatureAccess(
  feature: LicenseFeature,
  flags: LicenseFlags,
  allowBypass: boolean,
): FeatureAccess {
  const isBypassed = flags.gatingEnabled && allowBypass && flags.bypassEnabled;
  const isProEffective = !flags.gatingEnabled || flags.proUnlocked || isBypassed;

  // All currently modeled premium features are pro-gated.
  if (feature === "presets" || feature === "fieldCustomization" || feature === "adFreeMode") {
    if (!flags.gatingEnabled) return { allowed: true, reason: "gating-disabled" };
    if (flags.proUnlocked) return { allowed: true, reason: "pro-unlocked" };
    if (isBypassed) return { allowed: true, reason: "bypass-enabled" };
    return { allowed: false, reason: "pro-required" };
  }

  return { allowed: isProEffective, reason: isProEffective ? "pro-unlocked" : "pro-required" };
}

export function LicenseProvider({
  adapter,
  children,
}: {
  adapter?: LicenseAdapter | null;
  children: React.ReactNode;
}) {
  const resolvedAdapter = useMemo(
    () => adapter ?? createFallbackLicenseAdapter(),
    [adapter],
  );
  const [flags, setFlags] = useState<LicenseFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);

  const loadFlags = useCallback(async () => {
    setIsLoading(true);

    try {
      const loadedFlags = await resolvedAdapter.load();
      setFlags(sanitizeFlags(loadedFlags, resolvedAdapter.allowBypass));
    } catch (error) {
      console.warn("Failed to load license flags, falling back to defaults.", error);
      setFlags(DEFAULT_FLAGS);
    } finally {
      setIsLoading(false);
    }
  }, [resolvedAdapter]);

  useEffect(() => {
    void loadFlags();
  }, [loadFlags]);

  const persistAndSet = useCallback(
    async (
      key: keyof LicenseFlags,
      enabled: boolean,
      persist: (value: boolean) => Promise<void>,
    ) => {
      const nextFlags = sanitizeFlags(
        { ...flags, [key]: enabled },
        resolvedAdapter.allowBypass,
      );

      setFlags(nextFlags);

      try {
        await persist(enabled);
      } catch (error) {
        console.warn(`Failed to persist ${key} flag, reloading license state.`, error);
        await loadFlags();
      }
    },
    [flags, resolvedAdapter.allowBypass, loadFlags],
  );

  const setGatingEnabled = useCallback(
    async (enabled: boolean) => {
      await persistAndSet(
        "gatingEnabled",
        enabled,
        resolvedAdapter.setGatingEnabled,
      );
    },
    [persistAndSet, resolvedAdapter.setGatingEnabled],
  );

  const setProUnlocked = useCallback(
    async (enabled: boolean) => {
      await persistAndSet("proUnlocked", enabled, resolvedAdapter.setProUnlocked);
    },
    [persistAndSet, resolvedAdapter.setProUnlocked],
  );

  const setBypassEnabled = useCallback(
    async (enabled: boolean) => {
      if (!resolvedAdapter.allowBypass) return;
      await persistAndSet("bypassEnabled", enabled, resolvedAdapter.setBypassEnabled);
    },
    [persistAndSet, resolvedAdapter],
  );

  const canAccessFeature = useCallback(
    (feature: LicenseFeature) =>
      computeFeatureAccess(feature, flags, resolvedAdapter.allowBypass),
    [flags, resolvedAdapter.allowBypass],
  );

  const isBypassed =
    flags.gatingEnabled && resolvedAdapter.allowBypass && flags.bypassEnabled;
  const isProEffective = !flags.gatingEnabled || flags.proUnlocked || isBypassed;
  const tier = flags.proUnlocked || isBypassed ? "pro" : "free";

  const value = useMemo<LicenseContextValue>(
    () => ({
      ...flags,
      tier,
      isBypassed,
      isProEffective,
      allowBypass: resolvedAdapter.allowBypass,
      isLoading,
      setGatingEnabled,
      setProUnlocked,
      setBypassEnabled,
      canAccessFeature,
      refresh: loadFlags,
    }),
    [
      flags,
      tier,
      isBypassed,
      isProEffective,
      resolvedAdapter.allowBypass,
      isLoading,
      setGatingEnabled,
      setProUnlocked,
      setBypassEnabled,
      canAccessFeature,
      loadFlags,
    ],
  );

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense(): LicenseContextValue {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within LicenseProvider");
  }
  return context;
}

export function useIsPro(): boolean {
  return useLicense().isProEffective;
}

export function useFeatureAccess(feature: LicenseFeature): FeatureAccess {
  const { canAccessFeature } = useLicense();
  return canAccessFeature(feature);
}
